/**
 * UK + Republic of Ireland vehicle registration plate validator and
 * normaliser. Permissive by design — used at a car-auction yard where
 * trade-plates, kit-car Q plates, and weathered/old vehicles all need to
 * be accepted; the goal is to catch obvious typos and normalise spacing/
 * casing, NOT to reject any format that might actually be roadworthy.
 *
 * Supported formats (in match-priority order, per validate()):
 *   GB_CURRENT  — 2001+ post-DVLA reform, e.g. "AB12 CDE"
 *                 (https://www.gov.uk/displaying-number-plates)
 *   GB_PREFIX   — 1983-2001, e.g. "A123 BCD" (accepts Q-prefix kit cars)
 *   GB_SUFFIX   — 1963-1983, e.g. "ABC 123D"
 *   NI          — 1966+, e.g. "GAZ 1234" (county code MUST contain I or Z)
 *                 (Wikipedia: Vehicle registration plates of Northern Ireland)
 *   IE_CURRENT  — 2013+, e.g. "131-D-12345"
 *                 (S.I. No. 542/2012, Revenue.ie)
 *   IE_PRE2013  — 1987-2012, e.g. "12-D-12345"
 *                 (S.I. No. 441/1986)
 *   GB_DATELESS — pre-1963 / personal plates, e.g. "ABC 123"
 *   TRADE       — UK trade plate, e.g. "R 1234"
 *
 * Out of scope: military (`12 AB 34`), diplomatic (`123 D 456`), and EU
 * imports retaining their original-country plates. If one of these is
 * entered the validator will return `{ valid: false }` and the calling
 * UX should allow the operator to submit anyway as a free-text override
 * (the modal stores raw input alongside the validation verdict for that
 * exact reason).
 *
 * Sources cited inline above each regex; the regex strings themselves
 * derive from ZionFox/DVLARegCheck (BSD-2-Clause) and billinghamj/vrm-node
 * (ISC) — both DVLA-verified and used in production by motor-trade SaaS.
 */

export type RegPlateFormat =
  | 'GB_CURRENT'
  | 'GB_PREFIX'
  | 'GB_SUFFIX'
  | 'NI'
  | 'IE_CURRENT'
  | 'IE_PRE2013'
  | 'GB_DATELESS'
  | 'TRADE';

export interface RegPlateValidationOk {
  valid: true;
  /** Discriminator for downstream display / search heuristics. */
  format: RegPlateFormat;
  /** Canonical display form — uppercased, with the conventional separators
   * for the format (single space for GB, hyphens for IE). Always safe to
   * persist + show. */
  normalised: string;
}

export interface RegPlateValidationFail {
  valid: false;
  /** Human-readable error suitable for inline display next to a TextInput.
   * Always present when `valid === false`. */
  error: string;
}

export type RegPlateValidation = RegPlateValidationOk | RegPlateValidationFail;

// `I` and `Z` are reserved for Northern Ireland county codes in the
// pre-2001 + current GB schemes — never legal as area letters on a
// mainland plate. Excluding them at the regex level lets us distinguish
// "PLOQ 99" (no, P is fine but L is a number-1 lookalike) from a valid
// plate. Q is reserved for kit cars / unknown-age imports and ONLY
// appears as a year-letter on the prefix scheme — we accept it there
// explicitly in GB_PREFIX_REGEX below.
const GB_AREA_LETTER = '[A-HJ-PR-Y]';
const GB_YEAR_LETTER_NO_Q = '[A-HJ-NPR-TV-Y]';
const GB_YEAR_LETTER_WITH_Q = '[A-HJ-NPQR-TV-Y]';
const GB_SERIAL_LETTER = '[A-HJ-PR-Z]';

// Compiled regex per format. The validator runs them in order; first match
// wins, so more-specific patterns appear before more-permissive ones.
// Source for the strict GB_CURRENT pattern (and the area / serial letter
// exclusions): https://github.com/ZionFox/DVLARegCheck (BSD-2-Clause).
const GB_CURRENT_REGEX = new RegExp(`^${GB_AREA_LETTER}{2}(\\d{2})${GB_SERIAL_LETTER}{3}$`);
const GB_PREFIX_REGEX = new RegExp(`^${GB_YEAR_LETTER_WITH_Q}\\d{1,3}${GB_SERIAL_LETTER}{3}$`);
const GB_SUFFIX_REGEX = new RegExp(`^${GB_SERIAL_LETTER}{3}\\d{1,3}${GB_YEAR_LETTER_NO_Q}$`);
const NI_REGEX = /^([A-Z]{2,3})(\d{1,4})$/;
const IE_CURRENT_REGEX = /^(\d{3})([A-Z]{1,2})(\d{1,6})$/;
const IE_PRE2013_REGEX = /^(\d{2})([A-Z]{1,2})(\d{1,6})$/;
const GB_DATELESS_REGEX_NUMBERS_FIRST = new RegExp(`^(\\d{1,4})(${GB_SERIAL_LETTER}{1,3})$`);
const GB_DATELESS_REGEX_LETTERS_FIRST = new RegExp(`^(${GB_SERIAL_LETTER}{1,3})(\\d{1,4})$`);
const TRADE_REGEX = /^([A-Z])(\d{4})$/;

// Age digits 00 / 01 / 50 were never issued on GB_CURRENT plates
// (the 51 first-issue covered Sep 2001-Feb 2002; the next March allocation
// was 02). Rejecting them catches the most common typo where someone enters
// "AB01" or "AB50" instead of the intended plate.
const GB_CURRENT_AGE_BLACKLIST = new Set(['00', '01', '50']);

// Stripping the operator's input down to "letters + digits only" is the
// shared first step for ALL format matchers. We re-introduce the canonical
// separators per-format on the way back out via `prettyPrint()` so the
// stored value is always in the conventional shape regardless of how the
// operator typed it ("ab12cde" → "AB12 CDE", "131d12345" → "131-D-12345").
function strip(raw: string): string {
  return raw.toUpperCase().replace(/[\s\-_]+/g, '');
}

function prettyGbCurrent(s: string): string {
  return `${s.slice(0, 4)} ${s.slice(4)}`;
}

function prettyGbPrefix(s: string): string {
  // year-letter + digits + 3-letter serial — split on the last 3 letters
  return `${s.slice(0, -3)} ${s.slice(-3)}`;
}

function prettyGbSuffix(s: string): string {
  // 3-letter serial + digits + year-letter — split after the first 3 letters
  return `${s.slice(0, 3)} ${s.slice(3)}`;
}

function prettyNi(s: string): string {
  const m = NI_REGEX.exec(s);
  return m ? `${m[1]} ${m[2]}` : s;
}

function prettyIe(s: string, yearLen: 2 | 3): string {
  // YY-CC-SSSSSS or YYH-CC-SSSSSS  (hyphens; Revenue.ie convention)
  const year = s.slice(0, yearLen);
  const rest = s.slice(yearLen);
  const m = /^([A-Z]{1,2})(\d{1,6})$/.exec(rest);
  return m ? `${year}-${m[1]}-${m[2]}` : s;
}

function prettyDatelessLettersFirst(letters: string, digits: string): string {
  return `${letters} ${digits}`;
}

function prettyDatelessNumbersFirst(digits: string, letters: string): string {
  return `${digits} ${letters}`;
}

function prettyTrade(s: string): string {
  return `${s.slice(0, 1)} ${s.slice(1)}`;
}

// NI county codes always contain I or Z in position 2 or 3 of the letter
// block (the leading letter is the serial; I/Z never appear there). This
// rule is what distinguishes a valid NI plate from a malformed GB-current
// plate where someone forgot the year digits.
function isNiLetterBlock(letters: string): boolean {
  if (letters.length < 2 || letters.length > 3) return false;
  for (let i = 1; i < letters.length; i++) {
    const ch = letters[i];
    if (ch === 'I' || ch === 'Z') return true;
  }
  return false;
}

/**
 * Validate and normalise a registration plate string entered by the
 * operator. Whitespace, hyphens, underscores and case are normalised
 * before format matching. On success the returned `normalised` field
 * is the canonical display form for the matched format.
 *
 * The validator NEVER throws on bad input. Empty / whitespace-only
 * input returns `{ valid: false, error: 'Please enter a registration plate' }`
 * so the caller can use the error string verbatim in the UI.
 */
export function validateRegistrationPlate(raw: string): RegPlateValidation {
  if (typeof raw !== 'string') {
    return { valid: false, error: 'Please enter a registration plate' };
  }
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return { valid: false, error: 'Please enter a registration plate' };
  }
  if (!/^[A-Za-z0-9\s\-_]+$/.test(trimmed)) {
    return {
      valid: false,
      error: 'Only letters, digits, spaces and hyphens are allowed',
    };
  }

  const s = strip(trimmed);
  if (s.length < 2) {
    return { valid: false, error: 'Too short to be a registration plate' };
  }
  if (s.length > 10) {
    return { valid: false, error: 'Too long to be a registration plate' };
  }

  // GB current (2001+) — the overwhelmingly most common case at UK auctions.
  // Tried first so a typo in a current plate gets the most specific error.
  {
    const m = GB_CURRENT_REGEX.exec(s);
    if (m && !GB_CURRENT_AGE_BLACKLIST.has(m[1]!)) {
      return { valid: true, format: 'GB_CURRENT', normalised: prettyGbCurrent(s) };
    }
  }

  // IE current (2013+) — three-digit year identifier. Disambiguated from
  // IE pre-2013 by the leading-digit count. Listed BEFORE the pre-2013
  // pattern because the regex is more restrictive (the looser two-digit
  // form would also match a three-digit-prefixed plate's last two digits).
  {
    const m = IE_CURRENT_REGEX.exec(s);
    if (m) return { valid: true, format: 'IE_CURRENT', normalised: prettyIe(s, 3) };
  }

  // IE pre-2013 — two-digit year identifier. Length 4-10.
  {
    const m = IE_PRE2013_REGEX.exec(s);
    if (m) return { valid: true, format: 'IE_PRE2013', normalised: prettyIe(s, 2) };
  }

  // NI 1966+ — 2-3 letters (county code contains I or Z) + 1-4 digits.
  // Tried before GB_PREFIX because both can produce a "letters + digits"
  // shape; the I/Z requirement is what disambiguates.
  {
    const m = NI_REGEX.exec(s);
    if (m && isNiLetterBlock(m[1]!)) {
      return { valid: true, format: 'NI', normalised: prettyNi(s) };
    }
  }

  // GB prefix (1983-2001) — year-letter + 1-3 digits + 3-letter serial.
  // Accepts Q-prefix kit cars and personal imports.
  {
    const m = GB_PREFIX_REGEX.exec(s);
    if (m) return { valid: true, format: 'GB_PREFIX', normalised: prettyGbPrefix(s) };
  }

  // GB suffix (1963-1983) — 3-letter serial + 1-3 digits + year-letter.
  {
    const m = GB_SUFFIX_REGEX.exec(s);
    if (m) return { valid: true, format: 'GB_SUFFIX', normalised: prettyGbSuffix(s) };
  }

  // Trade plate — single letter + 4 digits. Distinct from prefix style by
  // having a 4-digit middle (prefix is max 3 digits).
  {
    const m = TRADE_REGEX.exec(s);
    if (m) return { valid: true, format: 'TRADE', normalised: prettyTrade(s) };
  }

  // Dateless / personal plates — letters-then-digits OR digits-then-letters.
  // Length is bounded (1-3 letters and 1-4 digits) to avoid matching
  // arbitrary nonsense. Listed LAST because the regex is the most
  // permissive — anything that fell through the earlier specific patterns
  // gets one last chance here.
  {
    const m = GB_DATELESS_REGEX_LETTERS_FIRST.exec(s);
    if (m) {
      return {
        valid: true,
        format: 'GB_DATELESS',
        normalised: prettyDatelessLettersFirst(m[1]!, m[2]!),
      };
    }
  }
  {
    const m = GB_DATELESS_REGEX_NUMBERS_FIRST.exec(s);
    if (m) {
      return {
        valid: true,
        format: 'GB_DATELESS',
        normalised: prettyDatelessNumbersFirst(m[1]!, m[2]!),
      };
    }
  }

  return {
    valid: false,
    error: 'Doesn’t look like a UK or Irish registration plate',
  };
}

/**
 * Normalise a plate for STORAGE / INDEXING. Strips all separators and
 * lowercases — the inverse of `prettyPrint*` above. This is the form we
 * write to the `regPlateNormalised` field on the DDB META row + into the
 * gsi-by-reg-plate index PK so that lookup by "AB12CDE", "ab12 cde",
 * "AB12-CDE" all hit the same row.
 *
 * Always returns lowercase regardless of input — the GSI partition key
 * needs to be case-insensitive and DDB string keys are case-sensitive,
 * so we case-fold at write+read time.
 *
 * Returns an empty string when the input is null/empty so callers can
 * pass through "user cleared the field" without a separate null check.
 */
export function normaliseRegPlateForStorage(raw: string | null | undefined): string {
  if (!raw) return '';
  return strip(raw).toLowerCase();
}
